// libs
import React, { memo, useEffect, useState, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';

// styles
import { Wrapper, TitleContainer, Title } from './styles';
import { CategorySwitchOverlay } from 'shared/components/GlobalSearch/components/AutoCompleteResults/styles';

// components
import Suggestions from 'shared/components/GlobalSearch/Desktop/components/Suggestions';
import StoreBanner from 'shared/components/StoreBanner';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// defs
import { ISuggestion } from 'shared/store/globalSearch/types';

interface IPopularOrTrendingProps {
  algo: string;
  title: string;
  suggestions: ISuggestion[];
  getData: (algo: string) => Promise<any>;
  isVisible: boolean;
}

function PopularOrTrending({
  algo,
  suggestions,
  getData,
  title,
  isVisible,
}: IPopularOrTrendingProps) {
  // @ts-ignore: any since store can't be hardcoded as enums
  const [activeCategorySwitch, setActiveCategorySwitch] = useState<{
    storeToRedirect: string;
    redirectUrl: string;
    productName: string;
  } | null>(null);

  const handleCategorySwitch = useCallback(
    (storeToRedirect: string, redirectUrl: string, productName: string) => {
      setActiveCategorySwitch({ storeToRedirect, redirectUrl, productName });
    },
    []
  );

  const dismissCategorySwitch = useCallback(() => setActiveCategorySwitch(null), []);

  useEffect(() => {
    return () => setActiveCategorySwitch(null);
  }, []);

  useEffect(() => {
    getData(algo);
  }, [algo, getData]);

  if (isEmpty(suggestions) || !isVisible) {
    return null;
  }

  if (activeCategorySwitch) {
    const { storeToRedirect, redirectUrl, productName } = activeCategorySwitch;
    return (
      <CategorySwitchOverlay>
        <StoreBanner
          storeToRedirect={storeToRedirect}
          currentStoreProductCount={0}
          isSearch={false}
          productName={productName}
          redirectUrl={redirectUrl}
          onDismiss={dismissCategorySwitch}
        />
      </CategorySwitchOverlay>
    );
  }

  return (
    <Wrapper data-test="PopularOrTrending-wrapper">
      <TitleContainer>
        <Title data-test="PopularOrTrending-title">
          <span>{title}</span>
        </Title>
      </TitleContainer>
      <Suggestions
        data={suggestions}
        searchType={algo}
        onCategorySwitch={handleCategorySwitch}
        data-test="PopularOrTrending-suggestions"
      />
    </Wrapper>
  );
}

export default memo(PopularOrTrending);
